/**
 * @file: FileRenderer.tsx
 *
 * A file attachment (`file` payload). Full form is a card with the filename,
 * size and a dog-eared extension badge; `quoted` is a compact badge + name.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadFile } from '@euler/model/chat';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { fileTypeBadge, formatBytes, stripHash } from './mediaLayout';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadFile> {
  onOpenUrl?: (url: string) => void;
}

export const FileRenderer: FC<Props> = memo(
  ({ payload, quoted, onOpenUrl }) => {
    const { rpx } = useResponsiveContext();
    const badge = fileTypeBadge(payload.filename);

    const handlePress = useCallback(
      () => onOpenUrl?.(stripHash(payload.url)),
      [onOpenUrl, payload.url],
    );

    if (quoted) {
      return (
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={handlePress}
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <View
            style={{
              width: rpx(18),
              height: rpx(22),
              borderRadius: rpx(3),
              borderTopRightRadius: rpx(7),
              backgroundColor: badge.color,
            }}
          />
          <Typography.Text size={rpx(13)} color="#475569" numberOfLines={1}>
            {payload.filename ?? '文件'}
          </Typography.Text>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={handlePress}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(12),
          width: rpx(250),
          padding: rpx(12),
          borderRadius: rpx(10),
          backgroundColor: '#fff',
          borderWidth: 1,
          borderColor: '#E2E8F0',
        }}
      >
        <View style={{ flex: 1 }}>
          <Typography.Text
            size={rpx(15)}
            color="#1E293B"
            numberOfLines={2}
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            {payload.filename ?? '文件'}
          </Typography.Text>
          {payload.sizeInBytes ? (
            <Typography.Text
              size={rpx(12)}
              color="#94A3B8"
              style={{ marginTop: rpx(4) }}
            >
              {formatBytes(payload.sizeInBytes)}
            </Typography.Text>
          ) : null}
        </View>
        {/* Dog-eared file badge with the extension label (PDF/DOC/…). */}
        <View
          style={{
            width: rpx(44),
            height: rpx(52),
            borderRadius: rpx(6),
            borderTopRightRadius: rpx(14),
            backgroundColor: badge.color,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography.Text
            size={rpx(12)}
            color="#fff"
            style={{ fontFamily: FontFamily.NotoSans.Bold, letterSpacing: 1 }}
          >
            {badge.label}
          </Typography.Text>
        </View>
      </TouchableOpacity>
    );
  },
);
